// Where this browser first came from, kept on the device so return visits can be counted by first source
// (.wiki/output/plan-paid-acquisition-2026-10-10.md). Saved once, on the first page this browser ever opens here,
// as a coarse class plus an optional campaign label from our own ad links. Nothing identifies the person.

export type Source = "paid-google" | "paid-other" | "search" | "direct" | "referral" | "shared" | "earlier"

const KEY = "dt:source"
const CAMPAIGN = "dt:campaign"

/** First-touch class from the landing URL and referrer. A click id or a paid utm_medium marks a paid visit. */
export function classify(url: URL, referrer: string): Source {
  const q = url.searchParams
  const medium = (q.get("utm_medium") ?? "").toLowerCase()
  const paid = /^(cpc|ppc|paid|paidsearch|paid-search|display|cpm)$/.test(medium)
  if (q.has("gclid") || q.has("gbraid") || q.has("wbraid") || (paid && /google/i.test(q.get("utm_source") ?? ""))) return "paid-google"
  if (paid || q.has("msclkid")) return "paid-other"
  // A link a player sent from a share button (lib/share.ts). Before fbclid: Facebook adds one to every outbound link.
  if (q.has("via")) return "shared"
  if (q.has("fbclid")) return "paid-other"
  if (!referrer) return "direct"
  let host = ""
  try { host = new URL(referrer).hostname } catch { return "direct" }
  if (host === url.hostname) return "direct"
  if (/(^|\.)(google|bing|duckduckgo|yahoo|ecosia|brave|yandex|baidu|startpage)\./.test(host)) return "search"
  return "referral"
}

/** Our own campaign label (utm_campaign), cut to the safe characters the event schema accepts; for a shared link,
 *  share-<channel> from its ?via=. */
export function campaignLabel(url: URL): string {
  const via = url.searchParams.get("via")
  const raw = via !== null && !url.searchParams.has("utm_campaign") ? `share-${via}` : url.searchParams.get("utm_campaign") ?? ""
  return raw.toLowerCase().replace(/[^a-z0-9-]/g, "-").slice(0, 30)
}

/** Records the first source once and returns the stored one. `hadRecord`: this browser had visited before this was
 *  added (it has a last-visit date but no source), so its true first source is unknown ("earlier"). */
export function firstTouch(url: URL, referrer: string, hadRecord: boolean): { source: Source; campaign: string } {
  try {
    const known = localStorage.getItem(KEY) as Source | null
    if (known) return { source: known, campaign: localStorage.getItem(CAMPAIGN) ?? "" }
    const source = hadRecord ? "earlier" : classify(url, referrer)
    const campaign = source.startsWith("paid") || source === "shared" ? campaignLabel(url) : ""
    localStorage.setItem(KEY, source)
    if (campaign) localStorage.setItem(CAMPAIGN, campaign)
    return { source, campaign }
  } catch {
    return { source: classify(url, referrer), campaign: "" }
  }
}

/** Distinct days this browser has visited (dt:visit-days, counted in lib/install-tip.ts), as a band. */
export function visitDaysBucket(n: number): string {
  if (n <= 1) return "1"
  if (n === 2) return "2"
  if (n <= 5) return "3-5"
  if (n <= 10) return "6-10"
  if (n <= 30) return "11-30"
  return "31+"
}
